import React, { FC, useEffect, useState } from 'react';
import { isEmailInvalid, redirectToNewTab } from '../../../helpers/general.helpers';
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Fab,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import useService from '../../../hooks/service.hook';
import UserService from '../../../services/user.service';
import DeviceRegistrationService from '../../../services/device-registration.service';
import AnalyticsService from '../../../services/analytics.service';
import { useSnackbar } from 'notistack';
import {
  AccountInformationForm,
  emptyAccountInformationForm,
  emptyPasswordForm,
  PasswordForm,
  FitnessDeviceConnection,
  notificationControls,
  ALLOW_MULTIPLE_DEVICE_CONNECTIONS,
} from '../settings.constants';
import { User } from '../../../models/user.models';
import { convertBlobToImageFile } from '../settings.helpers';
import RemoveIcon from '@mui/icons-material/Remove';
import CropIcon from '@mui/icons-material/Crop';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import ImageCropDialog from '../../../components/dialogs/image-crop.dialog';
import AppleWatchInstructionsDialog from '../../../components/dialogs/apple-watch-instructions.dialog';
import { REACT_APP_BUILD_ENV } from '../../../constants/server.constants';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import { UserAvatar } from '../../../components/user-avatar/user-avatar.component';
import { FieldErrors, useFieldErrors } from '../../../components/form-validation';

interface CommonCardActionsProps {
  errorMessage?: string;
  primaryButtonDisabled: boolean;
  primaryButtonOnClick(): any;
  secondaryButtonDisabled: boolean;
  secondaryButtonOnClick(): any;
}

const CommonCardActions: FC<CommonCardActionsProps> = (props) => (
  <CardActions className="card-actions">
    {props.errorMessage && (
      <span className="error" style={{ maxWidth: 127, overflow: 'scroll' }}>
        {props.errorMessage}
      </span>
    )}
    <Button
      variant="contained"
      disabled={props.secondaryButtonDisabled}
      color="error"
      onClick={props.secondaryButtonOnClick}
    >
      Cancel
    </Button>
    <Button variant="outlined" disabled={props.primaryButtonDisabled} onClick={props.primaryButtonOnClick}>
      Update
    </Button>
  </CardActions>
);

interface CommonCardHeaderProps {
  cardHeaderTitle: string;
  editAction(): any;
  editActionDisabled: boolean;
}

const CommonCardHeader: FC<CommonCardHeaderProps> = ({ cardHeaderTitle, editAction, editActionDisabled }) => (
  <CardHeader
    title={cardHeaderTitle}
    action={
      <IconButton aria-label="edit" onClick={editAction} disabled={editActionDisabled}>
        <EditIcon color={editActionDisabled ? 'disabled' : 'primary'} />
      </IconButton>
    }
  />
);

const CommonLoadingComponent = () => (
  <div className="loading-container">
    <CircularProgress style={{ width: 100, height: 100 }} />
  </div>
);

const PersonalInfoPage: FC = () => {
  // hooks
  const userService = useService(UserService);
  const deviceRegistrationService = useService(DeviceRegistrationService);
  const analyticsService = useService(AnalyticsService);
  const { enqueueSnackbar } = useSnackbar();
  const { showSnackbar } = useSnackbarHelper();

  // Fallback logo URLs
  const DEVICE_LOGO_FALLBACKS: { [key: string]: string } = {
    polar: 'https://staging.coelab.co/partners/polar_logo.png',
    garmin: 'https://staging.coelab.co/partners/garmin_logo.png',
    suunto: 'https://staging.coelab.co/partners/suunto_logo.png',
    strava: 'https://staging.coelab.co/partners/strava_logo.png',
    coros: 'https://staging.coelab.co/partners/coros_logo.png',
    apple_watch: '/partners/applewatch.png',
  };

  // states
  const [editAccount, setEditAccount] = useState<boolean>(false);
  const [cropImageDialogOpen, setCropImageDialogOpen] = useState<boolean>(false);
  const [appleWatchDialogOpen, setAppleWatchDialogOpen] = useState<boolean>(false);
  const [editPassword, setEditPassword] = useState<boolean>(false);
  const [editMeasuringSystem, setEditMeasuringSystem] = useState<boolean>(false);
  const [accountForm, setAccountForm] = useState<AccountInformationForm>(emptyAccountInformationForm);
  const [currUser, setCurrUser] = useState<AccountInformationForm>(emptyAccountInformationForm);
  const [currMeasuringSystem, setCurrMeasuringSystem] = useState<string>('');
  const [measuringSystemForm, setMeasuringSystemForm] = useState<string>('');
  const [passwordForm, setPasswordForm] = useState<PasswordForm>(emptyPasswordForm);
  const [isAccountLoading, setIsAccountLoading] = useState<boolean>(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState<boolean>(false);
  const [isMeasurementSystemLoading, setIsMeasurementSystemLoading] = useState<boolean>(false);
  const [isNotificationPreferencesLoading, setIsNotificationPreferencesLoading] = useState<boolean>(false);
  type AccountField = 'firstname' | 'lastname' | 'email' | 'birthday' | 'sex';
  type PasswordField = 'currentPassword' | 'newPassword' | 'confirmNewPassword';

  const {
    fieldErrors: accountFieldErrors,
    setFieldErrors: setAccountFieldErrors,
    resetFieldErrors: resetAccountFieldErrors,
  } = useFieldErrors<AccountField>();
  const {
    fieldErrors: passwordFieldErrors,
    setFieldErrors: setPasswordFieldErrors,
    resetFieldErrors: resetPasswordFieldErrors,
  } = useFieldErrors<PasswordField>();
  const [isRegisteredConnectionsLoading, setIsRegisteredConnectionsLoading] = useState<boolean>(true);
  const [connections, setConnections] = useState<FitnessDeviceConnection[]>([]);
  const [editConnections, setEditConnections] = useState<boolean>(false);
  // TEMP (COE): while multi-device connections are disabled, lock the "add device" options
  // once the user already has at least one device connected. See settings.constants.ts.
  const hasConnectedDevice = connections.some((connection: FitnessDeviceConnection) => connection.connected);
  const isAddDeviceLocked = !ALLOW_MULTIPLE_DEVICE_CONNECTIONS && hasConnectedDevice;
  const addDeviceLockedHint =
    'You can only have one device connected at a time. Please disconnect your current device to add a new one to CoeLab';
  const [editNotificationPreferences, setEditNotificationPreferences] = useState<boolean>(false);
  const [currNotificationPreferences, setCurrNotificationPreferences] = useState<any>({});
  const [notificationPreferences, setNotificationPreferences] = useState<any>({});
  const [isHeartRateZonesLoading, setIsHeartRateZonesLoading] = useState<boolean>(false);
  const [editHeartRateZones, setEditHeartRateZones] = useState<boolean>(false);
  const [currHeartRateZones, setCurrHeartRateZones] = useState<any[]>([]);
  const [heartRateZonesForm, setHeartRateZonesForm] = useState<any[]>([]);
  const [editAnnualStartDate, setEditAnnualStartDate] = useState<boolean>(false);
  const [currAnnualStartDate, setCurrAnnualStartDate] = useState<Dayjs | null>(null);
  const [annualStartDateForm, setAnnualStartDateForm] = useState<Dayjs | null>(null);
  const [isAnnualStartDateLoading, setIsAnnualStartDateLoading] = useState<boolean>(false);

  // componentDidMount
  useEffect(() => {
    getRegisteredConnections();
    getNotificationPreferences();
    getHeartRateZones();
    getAnnualStartDate();

    const existingUser: User = JSON.parse(localStorage.getItem('user') as string);

    setCurrUser({
      firstname: existingUser.firstName,
      lastname: existingUser.lastName,
      email: existingUser.email,
      profileImage: existingUser.profileImage,
      birthday: existingUser.birthday,
      sex: existingUser.sex,
    });
    setAccountForm({
      firstname: existingUser.firstName,
      lastname: existingUser.lastName,
      email: existingUser.email,
      profileImage: existingUser.profileImage,
      birthday: existingUser.birthday,
      sex: existingUser.sex,
    });
    setCurrMeasuringSystem(existingUser.measurementSystem);
    setMeasuringSystemForm(existingUser.measurementSystem);
  }, []);

  const getRegisteredConnections = () => {
    setIsRegisteredConnectionsLoading(true);
    userService
      .getRegisteredConnections()
      .then((response) => {
        setConnections(response.data.data);
        setIsRegisteredConnectionsLoading(false);
      })
      .catch(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Error while getting your device connections', 'error');
      });
  };

  const getNotificationPreferences = () => {
    setIsNotificationPreferencesLoading(true);
    userService.getNotificationPreferences().then((response) => {
      setIsNotificationPreferencesLoading(false);
      setCurrNotificationPreferences(response.data.data);
      setNotificationPreferences(response.data.data);
    });
  };

  const getHeartRateZones = () => {
    setIsHeartRateZonesLoading(true);
    userService.getHeartRateZones().then((response) => {
      setIsHeartRateZonesLoading(false);
      setCurrHeartRateZones(response.data.data);
      setHeartRateZonesForm(response.data.data);
    });
  };

  const getAnnualStartDate = () => {
    analyticsService
      .getAnalyticsAnnualStartDate()
      .then((response) => {
        const annualStartDate = response.data.data?.annual_start_date;
        let initialAnnualStartDate: Dayjs;

        if (annualStartDate) {
          // Preserve month and day from stored date, but set year to current year
          const storedDate = dayjs(annualStartDate);
          const currentYear = dayjs().year();
          initialAnnualStartDate = storedDate.year(currentYear);
        } else {
          initialAnnualStartDate = dayjs().startOf('year');
        }

        setCurrAnnualStartDate(initialAnnualStartDate);
        setAnnualStartDateForm(initialAnnualStartDate);
      })
      .catch(() => {
        const defaultDate = dayjs().startOf('year');
        setCurrAnnualStartDate(defaultDate);
        setAnnualStartDateForm(defaultDate);
      });
  };

  const updateHeartRateZones = () => {
    setIsHeartRateZonesLoading(true);
    userService
      .updateHeartRateZones(heartRateZonesForm)
      .then((response) => {
        setEditHeartRateZones(false);
        setIsHeartRateZonesLoading(false);
        setCurrHeartRateZones(response.data.data);
        setHeartRateZonesForm(response.data.data);
        showSnackbar('Successfully updated heart rate zones.', 'success');
      })
      .catch(() => {
        setEditHeartRateZones(false);
        setIsHeartRateZonesLoading(false);
        setHeartRateZonesForm(currHeartRateZones);
        showSnackbar('An error occured while updating your heart rate zones. Please try again later.', 'error');
      });
  };

  const cancelUpdateHeartRateZones = () => {
    setEditHeartRateZones(false);
    setHeartRateZonesForm(currHeartRateZones);
  };

  const updateAccount = async () => {
    resetAccountFieldErrors();
    if (validateAccount()) {
      setIsAccountLoading(true);

      let imageFile = undefined;

      // fix bug here if user did not upload an image
      if (accountForm.profileImage) {
        imageFile = await convertBlobToImageFile(accountForm.profileImage);
      }

      userService
        .updateUser({ ...accountForm, profileImage: imageFile })
        .then((response) => {
          setIsAccountLoading(false);
          setEditAccount(false);
          setCurrUser({
            firstname: response.data.data.firstName,
            lastname: response.data.data.lastName,
            email: response.data.data.email,
            profileImage: response.data.data.profileImage,
            birthday: response.data.data.birthday,
            sex: response.data.data.sex,
          });
          setAccountForm({
            firstname: response.data.data.firstName,
            lastname: response.data.data.lastName,
            email: response.data.data.email,
            profileImage: response.data.data.profileImage,
            birthday: response.data.data.birthday,
            sex: response.data.data.sex,
          });

          // update local storage items
          const existingUser: User = JSON.parse(localStorage.getItem('user') as string);
          existingUser.firstName = response.data.data.firstName;
          existingUser.lastName = response.data.data.lastName;
          existingUser.email = response.data.data.email;
          existingUser.profileImage = response.data.data.profileImage;
          existingUser.birthday = response.data.data.birthday;
          existingUser.sex = response.data.data.sex;
          localStorage.setItem('user', JSON.stringify(existingUser));

          enqueueSnackbar('Successfully updated acccount. Refresh to see your changes in CoeLab.', {
            autoHideDuration: 3000,
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            action: () => (
              <>
                <Button size="small" onClick={() => window.location.reload()} style={{ color: 'white' }}>
                  Refresh
                </Button>
              </>
            ),
          });
        })
        .catch(() => {
          setIsAccountLoading(false);
          setEditAccount(false);
          setAccountForm(currUser);
          showSnackbar('An error occured while updating your information. Please try again later', 'error');
        });
    }
  };

  const validateAccount = (): boolean => {
    const errors: FieldErrors<AccountField> = {};
    if (accountForm.firstname.length < 1) errors.firstname = 'First name is required';
    if (accountForm.lastname.length < 1) errors.lastname = 'Last name is required';
    if (accountForm.email.length < 1) {
      errors.email = 'Email is required';
    } else if (isEmailInvalid(accountForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!accountForm.birthday) errors.birthday = 'Birthday is required';
    if (!accountForm.sex) errors.sex = 'Sex is required';
    setAccountFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const cancelUpdateAccount = () => {
    setEditAccount(false);
    setAccountForm(currUser);
    resetAccountFieldErrors();
  };

  const updatePassword = () => {
    resetPasswordFieldErrors();
    if (validatePassword()) {
      setIsPasswordLoading(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      userService
        .updatePassword(passwordForm.currentPassword, passwordForm.newPassword)
        .then(() => {
          setEditPassword(false);
          setIsPasswordLoading(false);
          showSnackbar('Successfully updated password.', 'success');
        })
        .catch((error) => {
          setEditPassword(false);
          setIsPasswordLoading(false);
          const updatePasswordErrorText =
            error?.response?.status === 401
              ? 'Password is incorrect. Please use the correct current password.'
              : 'There was an error while updating your password, please try again later.';
          showSnackbar(updatePasswordErrorText, 'error');
        });
    }
  };

  const validatePassword = (): boolean => {
    const errors: FieldErrors<PasswordField> = {};
    if (!passwordForm.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.confirmNewPassword !== passwordForm.newPassword) {
      errors.confirmNewPassword = 'Passwords do not match';
    }
    if (!passwordForm.confirmNewPassword && !errors.confirmNewPassword) {
      errors.confirmNewPassword = 'Please confirm your new password';
    }
    setPasswordFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const cancelUpdatePassword = () => {
    setEditPassword(false);
    resetPasswordFieldErrors();
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    resetPasswordFieldErrors();
  };

  const cancelUpdateMeasuringSystem = () => {
    setEditMeasuringSystem(false);
    setMeasuringSystemForm(currMeasuringSystem);
  };

  const updateMeasuringSystem = () => {
    setIsMeasurementSystemLoading(true);
    userService
      .updateMeasurementSystem(measuringSystemForm)
      .then((response) => {
        const userInfo = response.data.data;

        setEditMeasuringSystem(false);
        setIsMeasurementSystemLoading(false);
        setCurrMeasuringSystem(userInfo.measurementSystem);
        setMeasuringSystemForm(userInfo.measurementSystem);

        // update local storage items
        const existingUser: User = JSON.parse(localStorage.getItem('user') as string);
        existingUser.measurementSystem = userInfo.measurementSystem;
        localStorage.setItem('user', JSON.stringify(existingUser));
      })
      .catch(() => {
        setEditMeasuringSystem(false);
        setIsMeasurementSystemLoading(false);
        setMeasuringSystemForm(currMeasuringSystem);
        showSnackbar('An error occured while updating your measurement system. Please try again later.', 'error');
      });
  };

  const cancelUpdateAnnualStartDate = () => {
    setEditAnnualStartDate(false);
    setAnnualStartDateForm(currAnnualStartDate);
  };

  const updateAnnualStartDate = () => {
    if (!annualStartDateForm) {
      return;
    }

    setIsAnnualStartDateLoading(true);
    const isoStringDate = annualStartDateForm.format('YYYY-MM-DD');

    analyticsService
      .updateAnalyticsAnnualStartDate(isoStringDate)
      .then((response) => {
        const updatedDate = response.data.data?.annual_start_date || isoStringDate;
        const dayjsDate = dayjs(updatedDate);
        // Ensure that year is set to current year
        const currentYear = dayjs().year();
        const dateWithCurrentYear = dayjsDate.year(currentYear);

        setEditAnnualStartDate(false);
        setIsAnnualStartDateLoading(false);
        setCurrAnnualStartDate(dateWithCurrentYear);
        setAnnualStartDateForm(dateWithCurrentYear);

        showSnackbar('Successfully updated annual start date.', 'success');
      })
      .catch(() => {
        setEditAnnualStartDate(false);
        setIsAnnualStartDateLoading(false);
        setAnnualStartDateForm(currAnnualStartDate);
        showSnackbar('An error occured while updating your annual start date. Please try again later.', 'error');
      });
  };

  const handleHeartRateZoneChange = (e, zoneId: number, bound: string) => {
    const newValue = parseInt(e.target.value);
    const adjoiningZoneId = bound === 'min' ? zoneId - 1 : zoneId + 1;
    const oppositeBound = bound === 'min' ? 'max' : 'min';
    const adjoiningZoneOffset = bound === 'min' ? -1 : 1;
    let zoneHasChanged = false;
    const newZones = currHeartRateZones
      .map((zone) => {
        if (zoneId === zone.zone_number) {
          if (
            (bound === 'min' && newValue <= zone[`${oppositeBound}_bpm`]) ||
            (bound === 'max' && newValue >= zone[`${oppositeBound}_bpm`])
          ) {
            zoneHasChanged = true;
            zone[`${bound}_bpm`] = newValue;
          }
        }
        return zone;
      })
      .map((zone) => {
        if (adjoiningZoneId === zone.zone_number) {
          if (zoneHasChanged) {
            zone[`${oppositeBound}_bpm`] = newValue + adjoiningZoneOffset;
          }
        }
        return zone;
      });
    setHeartRateZonesForm(newZones);
  };

  const handleFileInputChange = (event: any) => {
    if (event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = function (event: any) {
        setAccountForm({ ...accountForm, profileImage: event.target.result });
      };

      reader.readAsDataURL(file);
    }
  };

  const handleCropImage = async (img: string) => {
    setCropImageDialogOpen(false);
    setAccountForm({ ...accountForm, profileImage: img });
  };

  const handleDeleteImage = () => {
    setAccountForm({ ...accountForm, profileImage: undefined });
  };

  const deregisterPolar = () => {
    setEditConnections(false);
    setIsRegisteredConnectionsLoading(true);
    deviceRegistrationService
      .deregisterPolarUser()
      .then(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Successfully removed Polar connection from CoeLab', 'success');
        getRegisteredConnections();
      })
      .catch(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Error while removing this connection please try again later', 'error');
      });
  };

  const deregisterGarmin = () => {
    setEditConnections(false);
    setIsRegisteredConnectionsLoading(true);
    deviceRegistrationService
      .deregisterGarminUser()
      .then(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Successfully removed Garmin connection from CoeLab', 'success');
        getRegisteredConnections();
      })
      .catch(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Error while removing this connection please try again later', 'error');
      });
  };

  const deregisterStrava = () => {
    setEditConnections(false);
    setIsRegisteredConnectionsLoading(true);
    deviceRegistrationService
      .deregisterStravaUser()
      .then(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Successfully removed Strava connection from CoeLab', 'success');
        getRegisteredConnections();
      })
      .catch(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Error while removing this connection please try again later', 'error');
      });
  };

  const deregisterCoros = () => {
    setEditConnections(false);
    setIsRegisteredConnectionsLoading(true);
    deviceRegistrationService
      .deregisterCorosUser()
      .then(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Successfully removed COROS connection from CoeLab', 'success');
        getRegisteredConnections();
      })
      .catch(() => {
        setIsRegisteredConnectionsLoading(false);
        showSnackbar('Error while removing this connection please try again later', 'error');
      });
  };

  const handleRemoveConnection = (id: string) => {
    switch (id) {
      case 'polar':
        deregisterPolar();
        break;

      case 'garmin':
        deregisterGarmin();
        break;

      case 'strava':
        deregisterStrava();
        break;

      case 'coros':
        deregisterCoros();
        break;

      default:
        return;
    }
  };

  function isImageURL(url: any) {
    // Define regular expression to match image file extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif)$/i;

    // Test if URL ends with an image file extension
    if (imageExtensions.test(url)) {
      return true;
    }

    // Test if URL starts with a data URI scheme for an image
    if (/^data:image\/(png|jpg|jpeg|gif);base64/.test(url)) {
      return true;
    }

    // If none of the tests match, assume the URL is not an image
    return false;
  }

  const renderImageOptions = () => {
    if (editAccount) {
      return (
        <>
          <Tooltip title="Upload Profile Image">
            <IconButton style={{ position: 'absolute', right: 0, bottom: -25 }} component="label">
              <PhotoCameraIcon color="primary" />
              <input hidden accept="image/*" type="file" onChange={handleFileInputChange} />
            </IconButton>
          </Tooltip>
          {accountForm.profileImage && isImageURL(accountForm.profileImage) && (
            <Tooltip title="Crop Profile Image">
              <IconButton
                style={{ position: 'absolute', right: -40, bottom: -25 }}
                component="label"
                onClick={() => setCropImageDialogOpen(true)}
              >
                <CropIcon color="primary" />
              </IconButton>
            </Tooltip>
          )}
          {accountForm.profileImage && (
            <Tooltip title="Delete Profile Image">
              <IconButton
                style={{ position: 'absolute', right: 0, bottom: -65 }}
                component="label"
                onClick={handleDeleteImage}
              >
                <ClearIcon color="error" />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    } else {
      return null;
    }
  };

  const handleNotificationPreferenceChange = (id: string, value: boolean) => {
    const newObj = { ...notificationPreferences };
    newObj[id] = value;
    setNotificationPreferences(newObj);
  };

  const updateNotificationPreferences = () => {
    setEditNotificationPreferences(false);
    setIsNotificationPreferencesLoading(true);
    userService
      .setNotificationPreferences(notificationPreferences)
      .then((resp) => {
        setIsNotificationPreferencesLoading(false);
        setNotificationPreferences(resp.data.data);
        setCurrNotificationPreferences(resp.data.data);
      })
      .catch(() => {
        setIsNotificationPreferencesLoading(false);
        setNotificationPreferences(currNotificationPreferences);
        showSnackbar('Error while updating notification preferences please try again later', 'error');
      });
  };

  const onDeviceSelected = (id: string) => {
    setIsRegisteredConnectionsLoading(true);
    const polarRedirect = connections.find((item) => item.id === 'polar')?.redirectUrl;
    const suuntoRedirect = connections.find((item) => item.id === 'suunto')?.redirectUrl;
    const stravaRedirect = connections.find((item) => item.id === 'strava')?.redirectUrl;
    const corosRedirect = connections.find((item) => item.id === 'coros')?.redirectUrl;
    switch (id) {
      case 'polar':
        setIsRegisteredConnectionsLoading(false);
        try {
          redirectToNewTab(polarRedirect as any);
        } catch (err) {
          enqueueSnackbar('Select continue to finish connecting your polar watch', {
            variant: 'info',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            action: () => (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    window.open(polarRedirect as any, '_blank');
                  }}
                  style={{ color: 'white' }}
                >
                  Continue
                </Button>
              </>
            ),
          });
        }
        break;
      case 'garmin':
        deviceRegistrationService
          .getGarminUnauthorizedRequestToken()
          .then((response) => {
            setIsRegisteredConnectionsLoading(false);
            try {
              redirectToNewTab(response.data.data.url);
            } catch (err) {
              enqueueSnackbar('Select continue to finish connecting your garmin watch', {
                variant: 'info',
                anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                action: () => (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        window.open(response.data.data.url, '_blank');
                      }}
                      style={{ color: 'white' }}
                    >
                      Continue
                    </Button>
                  </>
                ),
              });
            }
          })
          .catch(() => {
            showSnackbar('There was an error while trying to sync Garmin. Please try again later', 'error');
            setIsRegisteredConnectionsLoading(false);
          });
        break;
      case 'suunto':
        setIsRegisteredConnectionsLoading(false);
        try {
          redirectToNewTab(suuntoRedirect as any);
        } catch (err) {
          enqueueSnackbar('Select continue to finish connecting your polar watch', {
            variant: 'info',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            action: () => (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    window.open(suuntoRedirect as any, '_blank');
                  }}
                  style={{ color: 'white' }}
                >
                  Continue
                </Button>
              </>
            ),
          });
        }
        break;
      case 'strava':
        setIsRegisteredConnectionsLoading(false);
        try {
          redirectToNewTab(stravaRedirect as any);
        } catch (err) {
          enqueueSnackbar('Select continue to finish connecting your Strava account', {
            variant: 'info',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            action: () => (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    window.open(stravaRedirect as any, '_blank');
                  }}
                  style={{ color: 'white' }}
                >
                  Continue
                </Button>
              </>
            ),
          });
        }
        break;
      case 'coros':
        setIsRegisteredConnectionsLoading(false);
        try {
          redirectToNewTab(corosRedirect as any);
        } catch (err) {
          enqueueSnackbar('Select continue to finish connecting your COROS account', {
            variant: 'info',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            action: () => (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    window.open(corosRedirect as any, '_blank');
                  }}
                  style={{ color: 'white' }}
                >
                  Continue
                </Button>
              </>
            ),
          });
        }
        break;
      default:
        return;
    }
  };

  return (
    <>
      <Card className="account-information-card">
        <CommonCardHeader
          cardHeaderTitle="Account Information"
          editAction={() => setEditAccount(true)}
          editActionDisabled={editAccount}
        />
        <CardContent className="card-content">
          <div style={{ position: 'relative' }}>
            <UserAvatar
              src={accountForm.profileImage}
              firstname={currUser.firstname}
              lastname={currUser.lastname}
              size={150}
              fontSize="3rem"
              sx={{ border: '0.5px solid black' }}
            />
            {renderImageOptions()}
          </div>
          {isAccountLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container">
              <TextField
                id="account-field-firstname"
                size="small"
                label="First Name"
                variant="outlined"
                disabled={!editAccount}
                value={accountForm.firstname}
                onChange={(evt) => {
                  setAccountForm({ ...accountForm, firstname: evt.target.value });
                  if (accountFieldErrors.firstname)
                    setAccountFieldErrors((p) => {
                      const n = { ...p };
                      delete n.firstname;
                      return n;
                    });
                }}
                error={!!accountFieldErrors.firstname}
                helperText={accountFieldErrors.firstname || ''}
              />
              <TextField
                id="account-field-lastname"
                size="small"
                label="Last Name"
                variant="outlined"
                disabled={!editAccount}
                value={accountForm.lastname}
                onChange={(evt) => {
                  setAccountForm({ ...accountForm, lastname: evt.target.value });
                  if (accountFieldErrors.lastname)
                    setAccountFieldErrors((p) => {
                      const n = { ...p };
                      delete n.lastname;
                      return n;
                    });
                }}
                error={!!accountFieldErrors.lastname}
                helperText={accountFieldErrors.lastname || ''}
              />
              <TextField
                id="account-field-email"
                size="small"
                label="Email"
                variant="outlined"
                disabled={!editAccount}
                value={accountForm.email}
                onChange={(evt) => {
                  setAccountForm({ ...accountForm, email: evt.target.value });
                  if (accountFieldErrors.email)
                    setAccountFieldErrors((p) => {
                      const n = { ...p };
                      delete n.email;
                      return n;
                    });
                }}
                error={!!accountFieldErrors.email}
                helperText={accountFieldErrors.email || ''}
              />
              <TextField
                id="account-field-birthday"
                label="Birthday"
                type="date"
                disabled={!editAccount}
                value={accountForm.birthday}
                onChange={(evt) => {
                  setAccountForm({ ...accountForm, birthday: evt.target.value });
                  if (accountFieldErrors.birthday)
                    setAccountFieldErrors((p) => {
                      const n = { ...p };
                      delete n.birthday;
                      return n;
                    });
                }}
                variant="outlined"
                size="small"
                error={!!accountFieldErrors.birthday}
                helperText={accountFieldErrors.birthday || ''}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth error={!!accountFieldErrors.sex}>
                <InputLabel id="sex" size="small">
                  Sex
                </InputLabel>
                <Select
                  labelId="sex"
                  id="sex"
                  disabled={!editAccount}
                  value={accountForm.sex}
                  label="Sex"
                  size="small"
                  style={{ textAlign: 'left' }}
                  inputProps={{ style: { textAlign: 'left' } }}
                  onChange={(evt) => {
                    setAccountForm({ ...accountForm, sex: evt.target.value });
                    if (accountFieldErrors.sex)
                      setAccountFieldErrors((p) => {
                        const n = { ...p };
                        delete n.sex;
                        return n;
                      });
                  }}
                >
                  <MenuItem value={'male'}>Male</MenuItem>
                  <MenuItem value={'female'}>Female</MenuItem>
                </Select>
                {accountFieldErrors.sex && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
                    {accountFieldErrors.sex}
                  </Typography>
                )}
              </FormControl>
            </div>
          )}
        </CardContent>
        <CommonCardActions
          errorMessage={Object.keys(accountFieldErrors).length > 0 ? 'Please fix the highlighted fields' : undefined}
          primaryButtonDisabled={!editAccount || isAccountLoading}
          primaryButtonOnClick={updateAccount}
          secondaryButtonDisabled={!editAccount || isAccountLoading}
          secondaryButtonOnClick={cancelUpdateAccount}
        />
      </Card>
      <Card className="password-card">
        <CommonCardHeader
          cardHeaderTitle="Password"
          editAction={() => setEditPassword(true)}
          editActionDisabled={editPassword}
        />
        <CardContent>
          {isPasswordLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container">
              <TextField
                id="pw-field-currentPassword"
                size="small"
                type="password"
                label="Current Password"
                variant="outlined"
                disabled={!editPassword}
                value={passwordForm.currentPassword}
                onChange={(evt) => {
                  setPasswordForm({ ...passwordForm, currentPassword: evt.target.value });
                  if (passwordFieldErrors.currentPassword)
                    setPasswordFieldErrors((p) => {
                      const n = { ...p };
                      delete n.currentPassword;
                      return n;
                    });
                }}
                error={!!passwordFieldErrors.currentPassword}
                helperText={passwordFieldErrors.currentPassword || ''}
              />
              <TextField
                id="pw-field-newPassword"
                size="small"
                type="password"
                label="New Password"
                variant="outlined"
                disabled={!editPassword}
                value={passwordForm.newPassword}
                onChange={(evt) => {
                  setPasswordForm({ ...passwordForm, newPassword: evt.target.value });
                  if (passwordFieldErrors.newPassword)
                    setPasswordFieldErrors((p) => {
                      const n = { ...p };
                      delete n.newPassword;
                      return n;
                    });
                }}
                error={!!passwordFieldErrors.newPassword}
                helperText={passwordFieldErrors.newPassword || ''}
              />
              <TextField
                id="pw-field-confirmNewPassword"
                size="small"
                type="password"
                label="Confirm New Password"
                variant="outlined"
                disabled={!editPassword}
                value={passwordForm.confirmNewPassword}
                onChange={(evt) => {
                  setPasswordForm({ ...passwordForm, confirmNewPassword: evt.target.value });
                  if (passwordFieldErrors.confirmNewPassword)
                    setPasswordFieldErrors((p) => {
                      const n = { ...p };
                      delete n.confirmNewPassword;
                      return n;
                    });
                }}
                error={!!passwordFieldErrors.confirmNewPassword}
                helperText={passwordFieldErrors.confirmNewPassword || ''}
              />
            </div>
          )}
        </CardContent>
        <CommonCardActions
          errorMessage={Object.keys(passwordFieldErrors).length > 0 ? 'Please fix the highlighted fields' : undefined}
          primaryButtonDisabled={!editPassword || isPasswordLoading}
          primaryButtonOnClick={updatePassword}
          secondaryButtonDisabled={!editPassword || isPasswordLoading}
          secondaryButtonOnClick={cancelUpdatePassword}
        />
      </Card>
      <Card className="measuring-system-card">
        <CommonCardHeader
          cardHeaderTitle="Device Connections"
          editAction={() => setEditConnections(true)}
          editActionDisabled={editConnections}
        />
        <CardContent>
          {isRegisteredConnectionsLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container">
              <div>
                <Typography variant="subtitle2">Connected Devices</Typography>
                <div style={{ width: '100%', display: 'flex', height: 95, flexWrap: 'wrap', gap: '8px' }}>
                  {connections
                    .filter((connection: FitnessDeviceConnection) => connection.connected)
                    .map((connection: FitnessDeviceConnection) => {
                      return (
                        <div key={connection.id} style={{ display: 'flex', position: 'relative' }}>
                          <Button onClick={() => redirectToNewTab(connection.redirectUrl)} style={{ marginRight: 4 }}>
                            <Paper
                              elevation={3}
                              style={{
                                padding: 4,
                                minWidth: '100px',
                                minHeight: '57px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={connection.logoUrl || DEVICE_LOGO_FALLBACKS[connection.id]}
                                alt={`${connection.name} logo`}
                                style={{
                                  maxWidth: '100px',
                                  maxHeight: '57px',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  const fallbackUrl = DEVICE_LOGO_FALLBACKS[connection.id];
                                  if (e.currentTarget.src !== fallbackUrl && fallbackUrl) {
                                    e.currentTarget.src = fallbackUrl;
                                  } else {
                                    e.currentTarget.style.display = 'none';
                                    if (e.currentTarget.parentElement) {
                                      e.currentTarget.parentElement.innerHTML = `<span style="font-size: 12px; padding: 8px;">${connection.name}</span>`;
                                    }
                                  }
                                }}
                              />
                            </Paper>
                          </Button>
                          <Fab
                            size="small"
                            color="error"
                            aria-label="remove"
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: -8,
                              display: editConnections ? 'flex' : 'none',
                            }}
                            onClick={() => handleRemoveConnection(connection.id)}
                          >
                            <RemoveIcon />
                          </Fab>
                        </div>
                      );
                    })}
                </div>
              </div>
              <div>
                <Typography variant="subtitle2">Connect a device to CoeLab</Typography>
                {isAddDeviceLocked && (
                  <Alert severity="info" style={{ marginTop: 4, marginBottom: 8 }}>
                    {addDeviceLockedHint}
                  </Alert>
                )}
                <div style={{ width: '100%', display: 'flex', height: 95, flexWrap: 'wrap', gap: '8px' }}>
                  {connections
                    .filter((connection: FitnessDeviceConnection) => !connection.connected)
                    .map((connection: FitnessDeviceConnection) => (
                      <Tooltip
                        key={connection.id}
                        title={isAddDeviceLocked ? addDeviceLockedHint : ''}
                        disableHoverListener={!isAddDeviceLocked}
                        disableFocusListener={!isAddDeviceLocked}
                        disableTouchListener={!isAddDeviceLocked}
                      >
                        <span>
                          <Button
                            disabled={isAddDeviceLocked}
                            onClick={() => onDeviceSelected(connection.id)}
                            style={{ opacity: isAddDeviceLocked ? 0.5 : 1 }}
                          >
                            <Paper
                              elevation={3}
                              style={{
                                padding: 4,
                                minWidth: '100px',
                                minHeight: '57px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={connection.logoUrl || DEVICE_LOGO_FALLBACKS[connection.id]}
                                alt={`${connection.name} logo`}
                                style={{
                                  maxWidth: '100px',
                                  maxHeight: '57px',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  const fallbackUrl = DEVICE_LOGO_FALLBACKS[connection.id];
                                  if (e.currentTarget.src !== fallbackUrl && fallbackUrl) {
                                    e.currentTarget.src = fallbackUrl;
                                  } else {
                                    e.currentTarget.style.display = 'none';
                                    if (e.currentTarget.parentElement) {
                                      e.currentTarget.parentElement.innerHTML = `<span style="font-size: 12px; padding: 8px;">${connection.name}</span>`;
                                    }
                                  }
                                }}
                              />
                            </Paper>
                          </Button>
                        </span>
                      </Tooltip>
                    ))}
                  <Tooltip
                    title={isAddDeviceLocked ? addDeviceLockedHint : ''}
                    disableHoverListener={!isAddDeviceLocked}
                    disableFocusListener={!isAddDeviceLocked}
                    disableTouchListener={!isAddDeviceLocked}
                  >
                    <span>
                      <Button
                        disabled={isAddDeviceLocked}
                        onClick={() => setAppleWatchDialogOpen(true)}
                        style={{ opacity: isAddDeviceLocked ? 0.5 : 1 }}
                      >
                        <Paper
                          elevation={3}
                          style={{
                            padding: 4,
                            minWidth: '100px',
                            minHeight: '57px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={DEVICE_LOGO_FALLBACKS.apple_watch}
                            alt="Apple Watch logo"
                            style={{
                              maxWidth: '100px',
                              maxHeight: '57px',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              if (e.currentTarget.parentElement) {
                                e.currentTarget.parentElement.innerHTML =
                                  '<span style="font-size: 12px; padding: 8px;">Apple Watch</span>';
                              }
                            }}
                          />
                        </Paper>
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </CardContent>
        <CardActions className="card-actions">
          <Button
            variant="contained"
            color="error"
            onClick={() => setEditConnections(false)}
            disabled={!editConnections}
          >
            Cancel
          </Button>
          <Button variant="outlined" color="primary" onClick={getRegisteredConnections}>
            Sync
          </Button>
        </CardActions>
      </Card>
      <Card className="annual-start-date-card">
        <CommonCardHeader
          cardHeaderTitle="Annual Start Date"
          editAction={() => setEditAnnualStartDate(true)}
          editActionDisabled={editAnnualStartDate}
        />
        <CardContent>
          {isAnnualStartDateLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container">
              <Typography style={{ color: 'gray', marginBottom: 20 }}>
                Select the start date for your annual training and analytics period.
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Annual Start Date"
                  value={annualStartDateForm}
                  onChange={(newValue: Dayjs | null) => {
                    if (newValue) {
                      // Preserve the year, only update month and day
                      const currentYear = annualStartDateForm ? annualStartDateForm.year() : dayjs().year();
                      const updatedDate = dayjs().year(currentYear).month(newValue.month()).date(newValue.date());
                      setAnnualStartDateForm(updatedDate);
                    } else {
                      setAnnualStartDateForm(newValue);
                    }
                  }}
                  views={['month', 'day']}
                  inputFormat="MMM DD"
                  disabled={!editAnnualStartDate}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={annualStartDateForm ? annualStartDateForm.format('MMM DD') : ''}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
          )}
        </CardContent>
        <CommonCardActions
          primaryButtonDisabled={!editAnnualStartDate || isAnnualStartDateLoading}
          primaryButtonOnClick={updateAnnualStartDate}
          secondaryButtonDisabled={!editAnnualStartDate || isAnnualStartDateLoading}
          secondaryButtonOnClick={cancelUpdateAnnualStartDate}
        />
      </Card>
      <Card className="annual-start-date-card">
        <CommonCardHeader
          cardHeaderTitle="Measuring System"
          editAction={() => setEditMeasuringSystem(true)}
          editActionDisabled={editMeasuringSystem}
        />
        <CardContent>
          {isMeasurementSystemLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container">
              <Typography style={{ color: 'gray' }}>
                Select which measuring system you want to use in CoeLab.
              </Typography>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={measuringSystemForm}
                onChange={(evt) => setMeasuringSystemForm(evt.target.value)}
                style={{ marginLeft: 30, marginTop: 30 }}
              >
                <FormControlLabel
                  value="imperial"
                  control={<Radio />}
                  label="Imperial"
                  disabled={!editMeasuringSystem}
                />
                <FormControlLabel value="metric" control={<Radio />} label="Metric" disabled={!editMeasuringSystem} />
              </RadioGroup>
            </div>
          )}
        </CardContent>
        <CommonCardActions
          primaryButtonDisabled={!editMeasuringSystem || isMeasurementSystemLoading}
          primaryButtonOnClick={updateMeasuringSystem}
          secondaryButtonDisabled={!editMeasuringSystem || isMeasurementSystemLoading}
          secondaryButtonOnClick={cancelUpdateMeasuringSystem}
        />
      </Card>
      <Card className="measuring-system-card" style={{ width: 600 }}>
        <CommonCardHeader
          cardHeaderTitle="Notification Preferences"
          editAction={() => setEditNotificationPreferences(true)}
          editActionDisabled={editNotificationPreferences}
        />
        <CardContent>
          {isNotificationPreferencesLoading ? (
            <CommonLoadingComponent />
          ) : (
            <div className="form-container" style={{ width: '100%' }}>
              <Typography style={{ color: 'gray' }}>
                Select when you want to receive notifications from CoeLab.
              </Typography>
              <div style={{ display: 'flex', height: 280 }}>
                <div className="form-container">
                  {notificationControls.column_1.map((obj) => (
                    <FormControlLabel
                      key={obj.value}
                      value={obj.value}
                      control={<Switch color="primary" checked={notificationPreferences[obj.value]} />}
                      onChange={(evt: any) => handleNotificationPreferenceChange(obj.value, evt.target.checked)}
                      label={obj.label}
                      labelPlacement="end"
                      disabled={!editNotificationPreferences}
                    />
                  ))}
                </div>
                <div className="form-container">
                  {notificationControls.column_2.map((obj) => (
                    <FormControlLabel
                      key={obj.value}
                      value={obj.value}
                      control={<Switch color="primary" checked={notificationPreferences[obj.value]} />}
                      onChange={(evt: any) => handleNotificationPreferenceChange(obj.value, evt.target.checked)}
                      label={obj.label}
                      labelPlacement="end"
                      disabled={!editNotificationPreferences}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
        <CommonCardActions
          primaryButtonDisabled={!editNotificationPreferences || isNotificationPreferencesLoading}
          primaryButtonOnClick={updateNotificationPreferences}
          secondaryButtonDisabled={!editNotificationPreferences || isNotificationPreferencesLoading}
          secondaryButtonOnClick={() => {
            setNotificationPreferences(currNotificationPreferences);
            setEditNotificationPreferences(false);
          }}
        />
      </Card>
      {(REACT_APP_BUILD_ENV === 'dev' || REACT_APP_BUILD_ENV === 'staging') && (
        <Card className="measuring-system-card">
          <CommonCardHeader
            cardHeaderTitle="Heart Rate Zones"
            editAction={() => setEditHeartRateZones(true)}
            editActionDisabled={editHeartRateZones}
          />
          <CardContent>
            {isHeartRateZonesLoading ? (
              <CommonLoadingComponent />
            ) : (
              <div className="form-container">
                <Typography sx={{ color: 'gray', mb: 1 }}>Select ranges for your heart rate zones.</Typography>
                <div style={{ display: 'flex', height: 280 }}>
                  <div className="form-container">
                    {[...Array(6).keys()]
                      .map((i) => i)
                      .map((zoneId) => (
                        <Box
                          key={zoneId}
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '4px' }}
                        >
                          <Typography
                            sx={{ color: !editHeartRateZones || isHeartRateZonesLoading ? 'grey' : 'black', ml: 1 }}
                          >{`Zone ${zoneId}`}</Typography>
                          <TextField
                            size="small"
                            sx={{ width: 80 }}
                            disabled={!editHeartRateZones || isHeartRateZonesLoading || zoneId === 0}
                            value={
                              zoneId === 0
                                ? 'Min'
                                : heartRateZonesForm.find((zone) => zoneId === zone.zone_number)?.min_bpm
                            }
                            onChange={(e) => handleHeartRateZoneChange(e, zoneId, 'min')}
                            type={zoneId === 0 ? '' : 'number'}
                            inputProps={{ onKeyDown: (event) => event.preventDefault() }}
                          />
                          <Typography sx={{ color: 'gray' }}>-</Typography>
                          <TextField
                            size="small"
                            sx={{ width: 80 }}
                            disabled={!editHeartRateZones || isHeartRateZonesLoading || zoneId === 5}
                            value={
                              zoneId === 5
                                ? 'Max'
                                : heartRateZonesForm.find((zone) => zoneId === zone.zone_number)?.max_bpm
                            }
                            onChange={(e) => handleHeartRateZoneChange(e, zoneId, 'max')}
                            type={zoneId === 5 ? '' : 'number'}
                            inputProps={{ onKeyDown: (event) => event.preventDefault() }}
                          />
                        </Box>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
          <CommonCardActions
            primaryButtonDisabled={!editHeartRateZones || isHeartRateZonesLoading}
            primaryButtonOnClick={updateHeartRateZones}
            secondaryButtonDisabled={!editHeartRateZones || isHeartRateZonesLoading}
            secondaryButtonOnClick={cancelUpdateHeartRateZones}
          />
        </Card>
      )}
      {cropImageDialogOpen && (
        <ImageCropDialog
          handleClose={() => setCropImageDialogOpen(false)}
          handleSubmit={handleCropImage}
          isOpen={cropImageDialogOpen}
          isLoading={false}
          image={accountForm.profileImage}
        />
      )}
      <AppleWatchInstructionsDialog open={appleWatchDialogOpen} onClose={() => setAppleWatchDialogOpen(false)} />
    </>
  );
};

export default PersonalInfoPage;
